import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ListingDetailsOverview } from "./components/ListingDetailsOverview";
import { ListingDetailsPayment } from "./components/ListingDetailsPayment";
import { ListingDetailsDelivery } from "./components/ListingDetailsDelivery";
import { ListingDetailsSeller } from "./components/ListingDetailsSeller";
import { ListingDetailsBuyer } from "./components/ListingDetailsBuyer";
import { useListingManagement } from "@/hooks/useListingManagement";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels } from "@/i18n/labels/adminLabels";
import {
  leadboxSheetContentClass,
  leadboxSheetSide,
} from "@/lib/native/nativeLeadboxSheet";
import { cn } from "@/lib/utils";

interface ListingDetailsPopupProps {
  isOpen: boolean;
  onClose: () => void;
  listingId: string | null;
}

export const ListingDetailsPopup = ({
  isOpen,
  onClose,
  listingId,
}: ListingDetailsPopupProps) => {
  const { getListingDetails } = useListingManagement();
  const {
    data: listingDetailsData,
    isLoading,
    error,
  } = getListingDetails(listingId || "");
  const { language } = useLanguage();
  const L = (adminLabels(language) as any)?.leadboxListings ?? {};

  const listing = listingDetailsData?.listing || null;

  if (!isOpen) {
    return null;
  }

  const buyerReservedText =
    listing?.listingType === "auction" ? L.highestBidder : L.potentialBuyer;
  return (
    <>
      <Sheet
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      >
        <SheetContent
          side={leadboxSheetSide()}
          className={cn(leadboxSheetContentClass(), "flex flex-col")}
        >
          <SheetHeader className="shrink-0">
            <SheetTitle>{L.listingDetails}</SheetTitle>
          </SheetHeader>

          {isLoading ? (
            <div className="flex items-center justify-center py-8 flex-1 min-h-[50dvh]">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-2">{L.loadingListingDetails}</span>
            </div>
          ) : (
            <Tabs defaultValue="overview" className="w-full py-2 flex flex-col flex-1 min-h-[58dvh]">
              <TabsList
                className={`grid w-full shrink-0 ${
                  listing?.status === "completed"
                    ? "grid-cols-5"
                    : listing?.status === "reserved"
                      ? "grid-cols-3"
                      : "grid-cols-2"
                }`}
              >
                <TabsTrigger value="overview">{L.overview}</TabsTrigger>
                <TabsTrigger value="seller">{L.seller}</TabsTrigger>
                {listing && listing.status === "reserved" && (
                  <TabsTrigger value="buyer">{buyerReservedText}</TabsTrigger>
                )}

                {listing && listing.status === "completed" && (
                  <>
                    <TabsTrigger value="buyer">{L.buyer}</TabsTrigger>
                    <TabsTrigger value="delivery">{L.delivery}</TabsTrigger>
                    <TabsTrigger value="payment">{L.payment}</TabsTrigger>
                  </>
                )}
              </TabsList>

              <TabsContent value="overview" className="space-y-6 flex-1 min-h-[48dvh] overflow-y-auto mt-2 data-[state=inactive]:hidden">
                <ListingDetailsOverview listing={listing} />
              </TabsContent>

              <TabsContent value="seller" className="space-y-4 flex-1 min-h-[48dvh] overflow-y-auto mt-2 data-[state=inactive]:hidden">
                <ListingDetailsSeller seller={listing?.seller} />
              </TabsContent>

              <TabsContent value="delivery" className="space-y-4 flex-1 min-h-[48dvh] overflow-y-auto mt-2 data-[state=inactive]:hidden">
                <ListingDetailsDelivery
                  transports={listing?.transport || []}
                  deliveryFee={listing?.deliveryFee}
                  listingId={listing?.id || ""}
                />
              </TabsContent>

              <TabsContent value="payment" className="space-y-4 flex-1 min-h-[48dvh] overflow-y-auto mt-2 data-[state=inactive]:hidden">
                <ListingDetailsPayment
                  payments={listing?.payment}
                  bidFee={listing?.bidFee}
                  buyer={listing?.winner ?? listing?.reservedFor ?? null}
                  seller={listing?.seller ?? null}
                />
              </TabsContent>

              {listing &&
                listing.status === "reserved" &&
                listing?.reservedFor != null && (
                  <>
                    <TabsContent value="buyer" className="space-y-4 flex-1 min-h-[48dvh] overflow-y-auto mt-2 data-[state=inactive]:hidden">
                      <ListingDetailsBuyer buyer={listing?.reservedFor} />
                    </TabsContent>
                  </>
                )}

              {listing &&
                listing.status === "completed" &&
                listing?.winner != null && (
                  <TabsContent value="buyer" className="space-y-4 flex-1 min-h-[48dvh] overflow-y-auto mt-2 data-[state=inactive]:hidden">
                    <ListingDetailsBuyer buyer={listing?.winner} />
                  </TabsContent>
                )}
            </Tabs>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};
